/**
 * User Matrix role checks for construction / fiber scheduling workflows.
 */
import {
  ADMINISTRATOR_TYPE_ID,
  DRAFTER_TYPE_ID,
  ENGINEERING_MANAGER_TYPE_ID,
  ENGINEERING_SUPERVISOR_TYPE_ID,
  canManageEngineeringDrawings,
  canViewerPermission,
  findMatrixUser,
  getViewerEmailFromStorage,
  getViewerRoleType,
  hasEffectiveAdminPrivileges,
  isViewerAdministrator,
  isViewerCoordinator,
  loadUserMatrixSettings,
  sameOrgMailbox,
} from "./userMatrixStorage";
import {
  isAccountantRoleType,
  isDrafterRoleType,
  isInspectorRoleType,
  isNetworkManagerRoleType,
  isNetworkTechRoleType,
  isOspEngineeringRoleType,
  isPermittingTabEditorRoleType,
  isSalesManagerRoleType,
  isDispatcher811RoleType,
  isSplicerRoleType,
  isSplicingManagerRoleType,
  isConstructionLeadRoleType,
  isConstructionManagerRoleType,
} from "./projectLandingTabAccess";

const CONSTRUCTION_SCHEDULE_EDITOR_ROLES = ["coordinator", "construction supervisor", "construction manager"];
const FIBER_SCHEDULE_EDITOR_ROLES = ["fiber supervisor"];
const NETWORKING_SCHEDULE_EDITOR_ROLES = ["coordinator", "network manager"];
const COMMERCIAL_PROJECT_CREATOR_ROLES = ["coordinator"];
const PROPOSAL_BUILDER_ROLES = ["sales manager", "sales representative"];

export function viewerRoleType(viewerEmail) {
  return getViewerRoleType(viewerEmail);
}

function roleNameMatches(type, patterns) {
  if (!type) return false;
  if (type.id === ADMINISTRATOR_TYPE_ID) return true;
  const name = String(type.name || "").trim().toLowerCase();
  return patterns.some((p) => {
    const pl = String(p).trim().toLowerCase();
    return name === pl || name.includes(pl);
  });
}

export function canAccessConstructionBoard(viewerEmail) {
  const email = String(viewerEmail || getViewerEmailFromStorage() || "").trim();
  return Boolean(email);
}

export function canEditConstructionSchedule(viewerEmail) {
  if (isViewerAdministrator(viewerEmail)) return true;
  if (isViewerCoordinator(viewerEmail)) return true;
  if (canViewerPermission(viewerEmail, "canEditConstructionSchedule", false)) return true;
  return roleNameMatches(getViewerRoleType(viewerEmail), CONSTRUCTION_SCHEDULE_EDITOR_ROLES);
}

export function canEditFiberSchedule(viewerEmail) {
  if (isViewerAdministrator(viewerEmail)) return true;
  if (canViewerPermission(viewerEmail, "canEditFiberSchedule", false)) return true;
  return roleNameMatches(getViewerRoleType(viewerEmail), FIBER_SCHEDULE_EDITOR_ROLES);
}

export function canEditNetworkingSchedule(viewerEmail) {
  if (isViewerAdministrator(viewerEmail)) return true;
  if (isViewerCoordinator(viewerEmail)) return true;
  // Network Technicians may work assigned installs, but must not schedule/add them.
  if (isNetworkTechRoleType(getViewerRoleType(viewerEmail))) return false;
  if (canViewerPermission(viewerEmail, "canEditNetworkingSchedule", false)) return true;
  return roleNameMatches(getViewerRoleType(viewerEmail), NETWORKING_SCHEDULE_EDITOR_ROLES);
}

export function canEditCalendarEventType(eventType, viewerEmail) {
  switch (eventType) {
    case "construction":
      return canEditConstructionSchedule(viewerEmail);
    case "splicing":
      return canEditFiberSchedule(viewerEmail);
    case "networking":
      return canEditNetworkingSchedule(viewerEmail);
    default:
      return false;
  }
}

export function canCreateCommercialProject(viewerEmail) {
  if (isViewerAdministrator(viewerEmail)) return true;
  if (isViewerCoordinator(viewerEmail)) return true;
  if (canViewerPermission(viewerEmail, "canCreateCommercialProject", false)) return true;
  return roleNameMatches(getViewerRoleType(viewerEmail), COMMERCIAL_PROJECT_CREATOR_ROLES);
}

/** Add sub-work orders to an existing parent (e.g. ongoing fiber incidents at 13227.50). */
export function canAddCommercialWorkOrderChild(viewerEmail) {
  if (canCreateCommercialProject(viewerEmail)) return true;
  return isPermittingTabEditorRoleType(getViewerRoleType(viewerEmail));
}

/** Add subsegments under an expansion segment parent work order. */
export function canAddExpansionSubsegment(viewerEmail) {
  return canAddCommercialWorkOrderChild(viewerEmail);
}

export function canAccessProposalBuilder(viewerEmail) {
  if (isViewerAdministrator(viewerEmail)) return true;
  if (canViewerPermission(viewerEmail, "canAccessProposalBuilder", false)) return true;
  return roleNameMatches(getViewerRoleType(viewerEmail), PROPOSAL_BUILDER_ROLES);
}

export function canMarkNetworkingInstallationComplete(viewerEmail, technicianEmail) {
  if (isViewerAdministrator(viewerEmail)) return true;
  if (hasEffectiveAdminPrivileges()) return true;
  const viewer = String(viewerEmail || getViewerEmailFromStorage() || "")
    .trim()
    .toLowerCase();
  const tech = String(technicianEmail || "").trim().toLowerCase();
  if (viewer && tech && sameOrgMailbox(viewer, tech)) return true;
  if (isViewerCoordinator(viewerEmail)) return true;
  if (isNetworkManagerRoleType(getViewerRoleType(viewerEmail))) return true;
  return roleNameMatches(getViewerRoleType(viewerEmail), ["coordinator", "network manager"]);
}

export function listMatrixUserEmailsByRoleName(roleNamePattern, roleTypeIds = []) {
  const patterns = (Array.isArray(roleNamePattern) ? roleNamePattern : [roleNamePattern])
    .map((p) => String(p || "").trim().toLowerCase())
    .filter(Boolean);
  const explicitIds = new Set(
    (Array.isArray(roleTypeIds) ? roleTypeIds : [roleTypeIds]).map((id) => String(id || "").trim()).filter(Boolean)
  );
  if (!patterns.length && explicitIds.size === 0) return [];
  const s = loadUserMatrixSettings();
  const typeIds = new Set(
    s.userTypes
      .filter((t) => {
        const id = String(t.id || "");
        if (explicitIds.has(id)) return true;
        const n = String(t.name || "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, " ");
        return patterns.some((pattern) => {
          if (n === pattern || n.includes(pattern)) return true;
          // "Engineering Supervisor" also matches "Eng Supervisor", "Supervisor (Engineering)", etc.
          if (pattern.includes("supervisor") && pattern.includes("engineering")) {
            return n.includes("supervisor") && n.includes("engineering");
          }
          return false;
        });
      })
      .map((t) => t.id)
  );
  if (typeIds.size === 0) return [];
  return s.users
    .filter((u) => typeIds.has(u.userTypeId) && u.status !== "pending")
    .map((u) => String(u.email || "").trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

/** Drafter assignment options: Drafter + Engineering Supervisor + Engineering Manager (by id and name). */
export function listDrafterAssigneeEmails() {
  return listMatrixUserEmailsByRoleName(
    ["Drafter", "Engineering Supervisor", "Engineering Manager"],
    [DRAFTER_TYPE_ID, ENGINEERING_SUPERVISOR_TYPE_ID, ENGINEERING_MANAGER_TYPE_ID]
  );
}

/** Network Technician / Network Manager — installation scheduling assignees. */
export const NETWORKING_INSTALLER_ROLE_PATTERNS = [
  "network technician",
  "network tech",
  "network manager",
];

export const EXPANSION_SEGMENT_FINANCIAL_ROLE_PATTERNS = ["administrator", "accountant"];

/** Administrators, managers, and accountants can open the Reporting page. */
export const REPORTING_ROLE_PATTERNS = ["administrator", "manager", "accountant"];

/** Datacenter floor planner — network managers and administrators. */
export const DATACENTER_LAYOUT_ROLE_PATTERNS = ["network manager", "administrator"];

/** Materials inventory page — engineering ops and inventory roles. */
export const INVENTORY_ROLE_PATTERNS = [
  "drafter",
  "engineering manager",
  "inventory specialist",
  "inventory",
];

export const EXPANSION_CONSTRUCTION_CONTRACT_ROLE_PATTERNS = [
  "administrator",
  "accountant",
  "construction manager",
  "project manager",
];

/** Permitting tab edit on commercial and expansion project landing pages. */
export const PERMITTING_TAB_EDITOR_ROLE_PATTERNS = [
  "permitting manager",
  "permitting specialist",
  "permitting specalist",
];

/** Engineering tab edit on commercial and expansion project landing pages. */
export const ENGINEERING_TAB_EDITOR_ROLE_PATTERNS = [
  "drafter",
  "engineering supervisor",
  "engineering manager",
];

/** Site walk files only — OSP Engineer on commercial project landing pages. */
export const OSP_ENGINEERING_SITE_WALK_EDIT_ROLE_PATTERNS = ["osp engineer"];

/** As-built uploads — Construction tab, or Engineering tab for OSP Engineer. */
export const ASBUILT_EDIT_ROLE_PATTERNS = ["construction manager", "osp engineer"];

/** USA / 811 ticket uploads on the Construction tab. */
export const USA_TICKET_EDIT_ROLE_PATTERNS = [
  "construction manager",
  "coordinator",
  "811 dispatcher",
];

/** Daily progress reports — Inspector, Construction Lead, OSP Engineer, and Construction Manager. */
export const INSPECTOR_DAILY_PROGRESS_EDIT_ROLE_PATTERNS = [
  "inspector",
  "construction lead",
  "osp engineer",
  "construction manager",
];

/** Assign the project inspector — Construction Manager on the Construction tab. */
export const INSPECTOR_ASSIGNMENT_EDIT_ROLE_PATTERNS = ["construction manager"];

/** Close commercial projects — Accountant only. */
export const ACCOUNTANT_CLOSE_PROJECT_ROLE_PATTERNS = ["accountant"];

/** Upload route — engineering managers (+ admin/coordinator). */
export const ENGINEERING_ROUTE_UPLOAD_ROLE_PATTERNS = ["engineering manager"];

/** Assign applicable drafter — engineering managers, supervisors, and drafters (+ admin/coordinator). */
export const ENGINEERING_DRAFTER_ASSIGNMENT_ROLE_PATTERNS = [
  "engineering manager",
  "engineering supervisor",
  "drafter",
];

/** Assign drafter from the Permitting tab — permitting managers and specialists (+ admin/coordinator). */
export const PERMITTING_DRAFTER_ASSIGNMENT_ROLE_PATTERNS = [
  ...PERMITTING_TAB_EDITOR_ROLE_PATTERNS,
];

/** Add/edit permits on the Engineering tab. */
export const ENGINEERING_PERMITS_EDIT_ROLE_PATTERNS = [
  "drafter",
  "engineering supervisor",
  "engineering manager",
];

/** Fields visible to any user when contracts plugin is enabled. */
export const EXPANSION_CONSTRUCTION_CONTRACT_PUBLIC_FIELD_KEYS = new Set([
  "construction_contractor",
  "fiber_contractor",
  "contract_execution_date",
  "contract_expiration_date",
]);

function roleNameMatchesPatterns(type, patterns) {
  if (!type) return false;
  if (type.id === ADMINISTRATOR_TYPE_ID) return true;
  const name = String(type.name || "").trim().toLowerCase();
  return patterns.some((p) => {
    const pl = String(p).trim().toLowerCase();
    return name === pl || name.includes(pl);
  });
}

export function canAccessExpansionFieldRoles(rolePatterns, viewerEmail, { includeCoordinator = false } = {}) {
  if (hasEffectiveAdminPrivileges()) return true;
  if (isViewerAdministrator(viewerEmail)) return true;
  if (includeCoordinator && isViewerCoordinator(viewerEmail)) return true;
  const patterns = Array.isArray(rolePatterns) ? rolePatterns : [];
  if (!patterns.length) return true;
  return roleNameMatchesPatterns(getViewerRoleType(viewerEmail), patterns);
}

export function expansionFieldRoleVisible(field, viewerEmail) {
  const patterns = field?.accessRoles;
  if (!Array.isArray(patterns) || patterns.length === 0) return true;
  const email = viewerEmail ?? getViewerEmailFromStorage();
  return canAccessExpansionFieldRoles(patterns, email);
}

export function canViewExpansionSegmentFinancials(viewerEmail) {
  return canAccessExpansionFieldRoles(EXPANSION_SEGMENT_FINANCIAL_ROLE_PATTERNS, viewerEmail, {
    includeCoordinator: true,
  });
}

/** Read-only budget fields for reporting (managers) and segment editors (admin/accountant). */
export function canViewExpansionSegmentFinancialsForReporting(viewerEmail) {
  return (
    canViewExpansionSegmentFinancials(viewerEmail) || canAccessExpansionFieldRoles(REPORTING_ROLE_PATTERNS, viewerEmail)
  );
}

export function canEditExpansionSegmentFinancials(viewerEmail) {
  return canViewExpansionSegmentFinancials(viewerEmail);
}

export function canViewFullExpansionConstructionContractFields(viewerEmail) {
  return canAccessExpansionFieldRoles(EXPANSION_CONSTRUCTION_CONTRACT_ROLE_PATTERNS, viewerEmail, {
    includeCoordinator: true,
  });
}

export function canViewExpansionConstructionContractFields(viewerEmail) {
  return true;
}

export function canEditExpansionConstructionContractFields(viewerEmail) {
  return canViewFullExpansionConstructionContractFields(viewerEmail);
}

/** Networking tab edit on commercial project landing pages. */
export const NETWORKING_TAB_EDITOR_ROLE_PATTERNS = [
  "network technician",
  "network tech",
  "network manager",
];

/**
 * Provisioning fields Network Technicians must not create or change
 * (coordinators / network managers / admins still can).
 * Installations are handled separately — techs may update existing rows
 * (e.g. mark complete) but must not add new ones.
 */
export const NETWORKING_TECH_RESTRICTED_FIELD_KEYS = new Set([
  "service_speed",
  "connection",
  "handoff",
  "ips",
]);

export function canEditPermittingTabFields(viewerEmail) {
  if (hasEffectiveAdminPrivileges()) return true;
  if (isViewerAdministrator(viewerEmail)) return true;
  if (isViewerCoordinator(viewerEmail)) return true;
  if (canViewerPermission(viewerEmail, "canEditPermittingTab", false)) return true;
  return isPermittingTabEditorRoleType(getViewerRoleType(viewerEmail));
}

/** Sales Manager (and permission holders) — commercial Overview / project summary only. */
export function canEditCommercialOverviewFields(viewerEmail) {
  if (hasEffectiveAdminPrivileges()) return true;
  if (isViewerAdministrator(viewerEmail)) return true;
  if (isViewerCoordinator(viewerEmail)) return true;
  if (canViewerPermission(viewerEmail, "canEditCommercialOverview", false)) return true;
  return isSalesManagerRoleType(getViewerRoleType(viewerEmail));
}

export function canEditEngineeringTabFields(viewerEmail) {
  if (hasEffectiveAdminPrivileges()) return true;
  if (isViewerAdministrator(viewerEmail)) return true;
  if (isViewerCoordinator(viewerEmail)) return true;
  if (canManageEngineeringDrawings(viewerEmail)) return true;
  if (isOspEngineeringRoleType(getViewerRoleType(viewerEmail))) return false;
  if (canViewerPermission(viewerEmail, "canEditEngineeringTab", false)) return true;
  return (
    isDrafterRoleType(getViewerRoleType(viewerEmail)) ||
    canAccessExpansionFieldRoles(ENGINEERING_TAB_EDITOR_ROLE_PATTERNS, viewerEmail)
  );
}

export function canEditOspEngineeringSiteWalkFiles(viewerEmail) {
  if (hasEffectiveAdminPrivileges()) return false;
  if (isViewerAdministrator(viewerEmail)) return false;
  if (isViewerCoordinator(viewerEmail)) return false;
  if (canViewerPermission(viewerEmail, "canEditOspUploads", false)) return true;
  return (
    isOspEngineeringRoleType(getViewerRoleType(viewerEmail)) ||
    canAccessExpansionFieldRoles(OSP_ENGINEERING_SITE_WALK_EDIT_ROLE_PATTERNS, viewerEmail)
  );
}

export function canEditConstructionAsbuiltFiles(viewerEmail) {
  if (hasEffectiveAdminPrivileges()) return false;
  if (isViewerAdministrator(viewerEmail)) return false;
  if (isViewerCoordinator(viewerEmail)) return false;
  if (canViewerPermission(viewerEmail, "canEditConstructionUploads", false)) return true;
  if (canViewerPermission(viewerEmail, "canEditOspUploads", false)) return true;
  return (
    isOspEngineeringRoleType(getViewerRoleType(viewerEmail)) ||
    canAccessExpansionFieldRoles(ASBUILT_EDIT_ROLE_PATTERNS, viewerEmail)
  );
}

export function canEditConstructionUsaTicketFiles(viewerEmail) {
  if (hasEffectiveAdminPrivileges()) return false;
  if (isViewerAdministrator(viewerEmail)) return false;
  if (isViewerCoordinator(viewerEmail)) return false;
  if (canViewerPermission(viewerEmail, "canEditUsaTickets", false)) return true;
  return (
    isDispatcher811RoleType(getViewerRoleType(viewerEmail)) ||
    canAccessExpansionFieldRoles(USA_TICKET_EDIT_ROLE_PATTERNS, viewerEmail)
  );
}

/** Scoped Construction tab upload edit (as-built, USA ticket, etc.). */
export function canEditConstructionTabUploads(viewerEmail) {
  return canEditConstructionAsbuiltFiles(viewerEmail) || canEditConstructionUsaTicketFiles(viewerEmail);
}

export function canEditInspectorDailyProgressReports(viewerEmail) {
  if (hasEffectiveAdminPrivileges()) return false;
  if (isViewerAdministrator(viewerEmail)) return false;
  if (isViewerCoordinator(viewerEmail)) return false;
  if (canViewerPermission(viewerEmail, "canEditInspectorDailyProgress", false)) return true;
  return (
    isInspectorRoleType(getViewerRoleType(viewerEmail)) ||
    isConstructionLeadRoleType(getViewerRoleType(viewerEmail)) ||
    isOspEngineeringRoleType(getViewerRoleType(viewerEmail)) ||
    isConstructionManagerRoleType(getViewerRoleType(viewerEmail)) ||
    canAccessExpansionFieldRoles(INSPECTOR_DAILY_PROGRESS_EDIT_ROLE_PATTERNS, viewerEmail)
  );
}

/** Construction Managers can edit any report row, not only ones they filed. */
export function canManageAllInspectorDailyProgressReports(viewerEmail) {
  if (isConstructionManagerRoleType(getViewerRoleType(viewerEmail))) return true;
  return canAccessExpansionFieldRoles(INSPECTOR_ASSIGNMENT_EDIT_ROLE_PATTERNS, viewerEmail);
}

export function canEditProjectInspectorAssignment(viewerEmail) {
  if (hasEffectiveAdminPrivileges()) return true;
  if (isViewerAdministrator(viewerEmail)) return true;
  if (isViewerCoordinator(viewerEmail)) return true;
  return (
    isConstructionManagerRoleType(getViewerRoleType(viewerEmail)) ||
    canAccessExpansionFieldRoles(INSPECTOR_ASSIGNMENT_EDIT_ROLE_PATTERNS, viewerEmail)
  );
}

export function canEditProjectDrafterAssignmentFromPermitting(viewerEmail) {
  if (hasEffectiveAdminPrivileges()) return true;
  if (isViewerAdministrator(viewerEmail)) return true;
  if (isViewerCoordinator(viewerEmail)) return true;
  return canAccessExpansionFieldRoles(PERMITTING_DRAFTER_ASSIGNMENT_ROLE_PATTERNS, viewerEmail);
}

export function canCloseCommercialProjectAsAccountant(viewerEmail) {
  if (hasEffectiveAdminPrivileges()) return false;
  if (isViewerAdministrator(viewerEmail)) return false;
  if (isViewerCoordinator(viewerEmail)) return false;
  if (canViewerPermission(viewerEmail, "canCloseCommercialProject", false)) return true;
  return (
    isAccountantRoleType(getViewerRoleType(viewerEmail)) ||
    canAccessExpansionFieldRoles(ACCOUNTANT_CLOSE_PROJECT_ROLE_PATTERNS, viewerEmail)
  );
}

export function isAccountantViewer(viewerEmail) {
  return canCloseCommercialProjectAsAccountant(viewerEmail);
}

export function isSplicerViewer(viewerEmail) {
  if (hasEffectiveAdminPrivileges()) return false;
  if (isViewerAdministrator(viewerEmail)) return false;
  if (isViewerCoordinator(viewerEmail)) return false;
  return isSplicerRoleType(getViewerRoleType(viewerEmail));
}

export function isNetworkTechViewer(viewerEmail) {
  if (hasEffectiveAdminPrivileges()) return false;
  if (isViewerAdministrator(viewerEmail)) return false;
  if (isViewerCoordinator(viewerEmail)) return false;
  return isNetworkTechRoleType(getViewerRoleType(viewerEmail));
}

export function canEditFiberTabFields(viewerEmail) {
  if (hasEffectiveAdminPrivileges()) return true;
  if (isViewerAdministrator(viewerEmail)) return true;
  if (isViewerCoordinator(viewerEmail)) return true;
  const role = getViewerRoleType(viewerEmail);
  const perms = role?.permissions;
  if (perms && Object.prototype.hasOwnProperty.call(perms, "canEditFiberTab")) {
    return Boolean(perms.canEditFiberTab);
  }
  return isSplicingManagerRoleType(role);
}

export function canEditNetworkingTabFields(viewerEmail) {
  if (hasEffectiveAdminPrivileges()) return true;
  if (isViewerAdministrator(viewerEmail)) return true;
  if (isViewerCoordinator(viewerEmail)) return true;
  if (canEditNetworkingSchedule(viewerEmail)) return true;
  if (canViewerPermission(viewerEmail, "canEditNetworkingTab", false)) return true;
  return isNetworkTechRoleType(getViewerRoleType(viewerEmail));
}

export function canEditAccountingTabFields(viewerEmail) {
  if (hasEffectiveAdminPrivileges()) return true;
  if (isViewerAdministrator(viewerEmail)) return true;
  if (isViewerCoordinator(viewerEmail)) return true;
  if (canViewerPermission(viewerEmail, "canEditAccountingTab", false)) return true;
  return (
    isAccountantRoleType(getViewerRoleType(viewerEmail)) ||
    canAccessExpansionFieldRoles(ACCOUNTANT_CLOSE_PROJECT_ROLE_PATTERNS, viewerEmail)
  );
}

/** True when the viewer may add/remove networking installation rows. */
export function canAddNetworkingInstallations(viewerEmail) {
  if (isNetworkTechViewer(viewerEmail)) return false;
  return canEditNetworkingTabFields(viewerEmail);
}

/** Read-only fiber files on the Networking tab for Network Technician. */
export function canViewNetworkingTabFiberFiles(viewerEmail) {
  return isNetworkTechViewer(viewerEmail);
}

export function expansionContractFieldVisible(field, viewerEmail) {
  if (!field?.derivedFromContract) {
    return expansionFieldRoleVisible(field, viewerEmail);
  }
  if (EXPANSION_CONSTRUCTION_CONTRACT_PUBLIC_FIELD_KEYS.has(field.key)) {
    return true;
  }
  return canViewFullExpansionConstructionContractFields(viewerEmail);
}

export function canEditExpansionRestrictedFields(viewerEmail) {
  return (
    canEditExpansionSegmentFinancials(viewerEmail) ||
    canEditExpansionConstructionContractFields(viewerEmail)
  );
}

/** Admin edits all fields; role holders only edit fields with matching accessRoles. */
export function canEditExpansionSchemaField(
  field,
  viewerEmail,
  {
    fullAdminEdit = false,
    tabRoleEdit = false,
    inspectorDailyProgressEdit = false,
    constructionAsbuiltEdit = false,
    networkingTabRoleEdit = false,
  } = {}
) {
  if (field?.derivedFromContract) return false;
  if (fullAdminEdit) return true;
  if (inspectorDailyProgressEdit && field?.key === "daily_progress_reports") {
    return true;
  }
  if (field?.key === "inspector") {
    const email = viewerEmail ?? getViewerEmailFromStorage();
    if (canEditProjectInspectorAssignment(email)) return true;
  }
  if (constructionAsbuiltEdit) {
    const editPatterns = field?.editRoles;
    if (Array.isArray(editPatterns) && editPatterns.length > 0) {
      return canAccessExpansionFieldRoles(editPatterns, viewerEmail ?? getViewerEmailFromStorage(), {
        includeCoordinator: true,
      });
    }
    return false;
  }
  if (networkingTabRoleEdit) {
    const email = viewerEmail ?? getViewerEmailFromStorage();
    if (
      NETWORKING_TECH_RESTRICTED_FIELD_KEYS.has(field?.key) &&
      isNetworkTechViewer(email)
    ) {
      return false;
    }
    return true;
  }
  const editPatterns = field?.editRoles;
  if (Array.isArray(editPatterns) && editPatterns.length > 0) {
    return canAccessExpansionFieldRoles(editPatterns, viewerEmail ?? getViewerEmailFromStorage(), {
      includeCoordinator: true,
    });
  }
  if (tabRoleEdit) return true;
  const patterns = field?.accessRoles;
  if (!Array.isArray(patterns) || patterns.length === 0) return false;
  return canAccessExpansionFieldRoles(patterns, viewerEmail ?? getViewerEmailFromStorage());
}
